import { NodePositionUpdate, NodeSwimlaneUpdate } from './bpmn-diagram.types'
import {
  findSwimlaneAt,
  isRectInsideSomeLane,
  overlapsAnyNode,
  toAbsolutePosition,
  toLaneId,
  toNodeId,
  toSwimlaneRelative,
} from './bpmn-graph.mapper'
import { shouldRevertMultiselectionDrop } from './multiselection.util'

import { CanvasData, NodeDefinitionRender } from '../../bpmn-canvas.types'
import { Point } from '../../dictionary/geometry.types'

type NodeDimensions = (type: NodeDefinitionRender['type']) => { width: number; height: number }

/** A released BPMN node at its final, grid-snapped, lane-relative position. */
export interface DroppedBpmnNode {
  /** React Flow id of the dragged node */
  id: string
  position: Point
  definition: NodeDefinitionRender
}

/** Local React Flow move: the node changes parent lane and/or lane-relative position. */
export interface LocalReparent {
  id: string
  parentId: string
  position: Point
}

/** The parts of a plan that are applied to React Flow and the host stores. */
export interface NodeDropCommit {
  swimlaneUpdates: NodeSwimlaneUpdate[]
  localReparents: LocalReparent[]
  movedRfIds: string[]
}

export interface NodeDropPlan {
  positionUpdates: NodePositionUpdate[]
  swimlaneUpdates: NodeSwimlaneUpdate[]
  localReparents: LocalReparent[]
  /** RF ids of nodes that actually changed position — their sequences get re-routed */
  movedRfIds: string[]
  crossLaneRfIds: string[]
  /** Dropped somewhere invalid, so they go back where they came from */
  snappedBackRfIds: string[]
}

type DropOutcome =
  | { kind: 'revert'; rfId: string; reparent: LocalReparent }
  | { kind: 'sameLane'; rfId: string; moved: boolean; positionUpdate: NodePositionUpdate }
  | { kind: 'crossLane'; rfId: string; swimlaneUpdate: NodeSwimlaneUpdate; reparent: LocalReparent }

interface DropCandidate {
  dropped: DroppedBpmnNode
  currentLane: NonNullable<ReturnType<typeof findSwimlaneAt>>
  absolutePosition: Point
  targetLane: ReturnType<typeof findSwimlaneAt>
  valid: boolean
}

const originalPosition = (definition: NodeDefinitionRender): Point => ({
  x: definition.layoutOptions?.x ?? 0,
  y: definition.layoutOptions?.y ?? 0,
})

function classifyDrop(
  dropped: DroppedBpmnNode,
  data: CanvasData,
  nodeDimensions: NodeDimensions,
  droppedDefinitionIds: Set<NodeDefinitionRender['id']>
): DropCandidate | null {
  const { definition } = dropped
  const currentLane = data.swimlanes.find(
    (swimlane) => swimlane.id === definition.swimlaneDefinitionId
  )
  if (!currentLane) {
    return null
  }
  const { width, height } = nodeDimensions(definition.type)
  const absolutePosition = toAbsolutePosition(dropped.position, currentLane)
  const absoluteCenter = {
    x: absolutePosition.x + width / 2,
    y: absolutePosition.y + height / 2,
  }
  const nodeRect = { ...absolutePosition, width, height }
  const targetLane = findSwimlaneAt(absoluteCenter, data.swimlanes)

  return {
    dropped,
    currentLane,
    absolutePosition,
    targetLane,
    valid:
      !!targetLane &&
      isRectInsideSomeLane(nodeRect, data.swimlanes) &&
      !overlapsAnyNode(nodeRect, droppedDefinitionIds, data, nodeDimensions),
  }
}

function resolveDrop(candidate: DropCandidate, invalidGroupDrop: boolean): DropOutcome {
  const { dropped, currentLane, absolutePosition, targetLane } = candidate
  const { definition } = dropped

  if (invalidGroupDrop || !candidate.valid || !targetLane) {
    return {
      kind: 'revert',
      rfId: dropped.id,
      reparent: {
        id: dropped.id,
        parentId: toLaneId(currentLane.id),
        position: originalPosition(definition),
      },
    }
  }

  if (targetLane.id === currentLane.id) {
    const original = originalPosition(definition)

    return {
      kind: 'sameLane',
      rfId: dropped.id,
      moved: dropped.position.x !== original.x || dropped.position.y !== original.y,
      positionUpdate: {
        node: definition,
        updates: {
          id: definition.id,
          layoutOptions: dropped.position,
          swimlaneDefinitionId: definition.swimlaneDefinitionId,
          isNavAreaAssigned: definition.isNavAreaAssigned,
        },
      },
    }
  }

  const relativeToTarget = toSwimlaneRelative(absolutePosition, targetLane)

  return {
    kind: 'crossLane',
    rfId: dropped.id,
    swimlaneUpdate: {
      node: definition,
      updates: {
        id: definition.id,
        layoutOptions: relativeToTarget,
        swimlaneDefinitionId: targetLane.id,
        isNavAreaAssigned: definition.isNavAreaAssigned,
      },
    },
    reparent: {
      id: dropped.id,
      parentId: toLaneId(targetLane.id),
      position: relativeToTarget,
    },
  }
}

/**
 * Classifies a released node drag: every dropped node either stays in its lane,
 * crosses into the lane under its center, or snaps back where it came from. Nodes
 * whose own lane is gone (a collaborator deleted it mid-drag) are left out entirely.
 */
export function planNodeDrop(
  droppedNodes: DroppedBpmnNode[],
  data: CanvasData,
  nodeDimensions: NodeDimensions
): NodeDropPlan {
  const droppedDefinitionIds = new Set(droppedNodes.map(({ definition }) => definition.id))
  const candidates = droppedNodes.map((dropped) =>
    classifyDrop(dropped, data, nodeDimensions, droppedDefinitionIds)
  )
  // A multiselection drop is atomic: if one member had an invalid drop, the
  // complete selection returns to its last valid position.
  const invalidGroupDrop = shouldRevertMultiselectionDrop(
    candidates.map((candidate) => !!candidate?.valid)
  )
  const outcomes = candidates
    .filter((candidate) => !!candidate)
    .map((candidate) => resolveDrop(candidate, invalidGroupDrop))

  return {
    positionUpdates: outcomes.flatMap((outcome) =>
      outcome.kind === 'sameLane' ? [outcome.positionUpdate] : []
    ),
    swimlaneUpdates: outcomes.flatMap((outcome) =>
      outcome.kind === 'crossLane' ? [outcome.swimlaneUpdate] : []
    ),
    localReparents: outcomes.flatMap((outcome) =>
      outcome.kind === 'sameLane' ? [] : [outcome.reparent]
    ),
    movedRfIds: outcomes.flatMap((outcome) =>
      outcome.kind === 'sameLane' && outcome.moved ? [outcome.rfId] : []
    ),
    crossLaneRfIds: outcomes.flatMap((outcome) =>
      outcome.kind === 'crossLane' ? [outcome.rfId] : []
    ),
    snappedBackRfIds: outcomes.flatMap((outcome) =>
      outcome.kind === 'revert' ? [outcome.rfId] : []
    ),
  }
}

/** Everything the plan asks for: cross-lane nodes move and re-route with the rest. */
export function commitNodeDrop(plan: NodeDropPlan): NodeDropCommit {
  return {
    swimlaneUpdates: plan.swimlaneUpdates,
    localReparents: plan.localReparents,
    movedRfIds: [...plan.movedRfIds, ...plan.crossLaneRfIds],
  }
}

/**
 * Declining the nav-area removal confirmation sends every cross-lane node of this
 * drag back to the lane and position it was dragged from; same-lane moves still commit.
 */
export function revertCrossLaneNodeDrop(plan: NodeDropPlan): NodeDropCommit {
  const cancelledRfIds = new Set(plan.crossLaneRfIds)

  return {
    swimlaneUpdates: [],
    localReparents: [
      ...plan.localReparents.filter((reparent) => !cancelledRfIds.has(reparent.id)),
      ...plan.swimlaneUpdates.map((update) => ({
        id: toNodeId(update.node.id),
        parentId: toLaneId(update.node.swimlaneDefinitionId),
        position: {
          x: update.node.layoutOptions?.x ?? 0,
          y: update.node.layoutOptions?.y ?? 0,
        },
      })),
    ],
    movedRfIds: plan.movedRfIds,
  }
}

/** True when a cross-lane node of this drag is assigned to a navigation area. */
export function requiresAreaRemovalConfirm(plan: NodeDropPlan): boolean {
  return plan.swimlaneUpdates.some((update) => update.node.isNavAreaAssigned)
}
